import { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks";
import { CabinetSummary, TaskItem } from "services/cabinet.service";
import { fuelMonthName, fuelTaskPeriod } from "features/cabinet-fuel-months/month-name";
import { fetchCabinetSummaryThunk } from "./cabinet-summary.slice";
import { expiryStatus, useMarkExpiryHandled } from "./document-expiry";
import TaskRow, { taskAge, Translate } from "./task-row";
import TasksSection from "./tasks-section";

// «To sign» and «Expiring soon» show their first rows only; the rest are one link away.
const PREVIEW_ROWS = 3;

// What a signature is called in the row. A kind not listed here reads as a plain document.
const SIGN_KIND_LABEL_KEYS: Record<string, string> = {
  asset_act: "tasks_kind_asset_act",
  work_safety: "tasks_kind_work_safety",
  leave: "tasks_kind_leave",
  fuel: "tasks_kind_fuel",
  trip: "tasks_kind_trip",
};

const LINK_CLASS = "inline-flex items-center text-sm text-indigo-600 hover:underline max-md:min-h-11";

type Queues = CabinetSummary["queues"];

/**
 * The line under the company-documents row: how they are signed and how long the
 * oldest has waited, then the documents that are signed on paper instead.
 */
function signWorkMeta(
  signWork: NonNullable<Queues["signWork"]>,
  paperWork: Queues["paperWork"],
  t: Translate
) {
  const { click, dokobit, oldestDays } = signWork;
  const lines = [t("tasks_meta_sign_work", { click, dokobit, days: oldestDays })];
  if (paperWork) lines.push(t("tasks_meta_paper", { count: paperWork.count }));
  return lines.join(" · ");
}

/** One list of rows inside a group. */
interface TaskPart {
  key: string;
  /** A small title over the rows; left out when the group is a single list. */
  title?: string;
  /** The whole number in this list; `rows` may be only what the summary returned of it. */
  count: number;
  rows: ReactNode[];
  /** The existing page with all of them; left out when the rows already lead there. */
  allPath?: string;
  /** One quiet line under the list. */
  note?: string;
}

interface TaskGroupProps {
  title: string;
  parts: TaskPart[];
  /** Rows shown per list; left out = every row the summary returned. */
  maxRows?: number;
  /** The text of the link to the full list, shown when a list has more than is shown. */
  allLinkKey: string;
}

/**
 * A titled group of task lists. The group's number is the sum of its lists. Each list
 * shows its rows (the first `maxRows` of them, if that is set) and links to the full
 * page when it holds more than is shown. A list with nothing in it is not drawn.
 */
function TaskGroup({ title, parts, maxRows, allLinkKey }: TaskGroupProps) {
  const { t } = useTranslation();
  const shownParts = parts.filter((part) => part.count > 0);
  if (shownParts.length === 0) return null;
  const total = shownParts.reduce((sum, part) => sum + part.count, 0);

  return (
    <section aria-label={title} className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">
        {title} ({total})
      </h3>
      {shownParts.map((part) => {
        const shownRows = part.rows.slice(0, maxRows);
        return (
          <div key={part.key} className="space-y-2">
            {part.title && (
              <h4 className="text-xs font-medium text-gray-500">
                {part.title} ({part.count})
              </h4>
            )}
            <ul className="space-y-2">{shownRows}</ul>
            {part.allPath && part.count > shownRows.length && (
              <Link to={part.allPath} className={LINK_CLASS}>
                {t(allLinkKey, { count: part.count })}
              </Link>
            )}
            {part.note && <p className="text-xs text-gray-500">{part.note}</p>}
          </div>
        );
      })}
    </section>
  );
}

interface MyTaskGroupsProps {
  summary: CabinetSummary;
  companyId: string;
  userId: string;
}

/**
 * «Mano»: what waits for the person themselves. Three groups, each drawn only
 * when it has something: to sign (their own documents, then the company documents
 * waiting in their work queue — one by one while there are a few, else one summary row),
 * to do (health check certificate, training, pinned posts, trip reports, fuel reports) and
 * documents about to expire (each can be marked «Sutvarkyta»).
 * With nothing in any of them the whole section is left out.
 */
export default function MyTaskGroups({ summary, companyId, userId }: MyTaskGroupsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const markExpiryHandled = useMarkExpiryHandled(() => dispatch(fetchCabinetSummaryThunk(companyId)));
  const { sign, training, pinned, tripReport, fuelReport, medical, expiring, notInApp } = summary.personal;
  const { signWork, paperWork } = summary.queues;
  const signWorkCount = signWork?.count ?? 0;
  const companyPath = (rest: string) => `/app/${companyId}${rest}`;
  const otherCompanyChip = (item: TaskItem) => (item.companyId === companyId ? [] : [item.companyName]);
  const needsCertificate = medical.state === "upload_needed";

  const signRow = (item: TaskItem) => {
    const kindLabel = t(SIGN_KIND_LABEL_KEYS[item.kind] ?? "tasks_kind_document");
    const signLabel = item.signType === "Dokobit" ? "tasks_action_sign_dokobit" : "tasks_action_sign";
    return (
      <TaskRow
        key={`sign-${item.id}`}
        title={item.title}
        meta={`${kindLabel} · ${taskAge(item.since, t)}`}
        chips={otherCompanyChip(item)}
        action={{ label: t(signLabel), onClick: () => navigate(item.link) }}
      />
    );
  };

  // The company documents the person signs for the company. While there are a few the
  // server sends them, and they show one by one like the person's own; more read as one
  // summary row. Either way the whole list is the work queue page. Papers are named but
  // not counted.
  const signWorkPath = companyPath("/my-work/pending-signature?type=work");
  const signWorkPart = (): TaskPart => {
    if (!signWork) return { key: "sign-work", count: 0, rows: [] };
    if (signWork.items) {
      return {
        key: "sign-work",
        title: t("tasks_queue_sign_work"),
        count: signWork.count,
        rows: signWork.items.map(signRow),
        allPath: signWorkPath,
        note: paperWork ? t("tasks_meta_paper", { count: paperWork.count }) : undefined,
      };
    }
    const summaryRow = (
      <TaskRow
        key="sign-work"
        title={`${t("tasks_queue_sign_work")} (${signWork.count})`}
        meta={signWorkMeta(signWork, paperWork, t)}
        action={{ label: t("tasks_action_open"), onClick: () => navigate(signWorkPath) }}
      />
    );
    return { key: "sign-work", count: signWork.count, rows: [summaryRow] };
  };

  // The fixed order of the kinds in «To do».
  const toDoParts: TaskPart[] = [
    {
      key: "medical",
      title: t("tasks_do_medical"),
      count: needsCertificate ? 1 : 0,
      rows: needsCertificate
        ? [
            <TaskRow
              key="medical"
              title={t("tasks_medical_upload_needed")}
              action={{
                label: t("tasks_action_upload"),
                onClick: () => navigate(companyPath(`/profile/${userId}/medical-check`)),
              }}
            />,
          ]
        : [],
      allPath: companyPath(`/profile/${userId}/medical-check`),
    },
    {
      key: "training",
      title: t("tasks_do_training"),
      count: training.count,
      rows: training.items.map((item) => (
        <TaskRow
          key={`training-${item.id}`}
          title={item.title}
          action={{ label: t("tasks_action_start"), onClick: () => navigate(item.link) }}
        />
      )),
      allPath: companyPath("/my-work/training"),
    },
    {
      key: "pinned",
      title: t("tasks_do_pinned"),
      count: pinned.count,
      rows: pinned.items.map((item) => (
        <TaskRow
          key={`pinned-${item.id}`}
          title={item.title}
          action={{ label: t("tasks_action_read"), onClick: () => navigate(item.link) }}
        />
      )),
      allPath: companyPath("/posts"),
    },
    {
      key: "trips",
      title: t("tasks_do_trip_reports"),
      count: tripReport.count,
      rows: tripReport.items.map((item) => (
        <TaskRow
          key={`trip-${item.id}`}
          title={item.title}
          action={{ label: t("tasks_action_fill_report"), onClick: () => navigate(item.link) }}
        />
      )),
      allPath: companyPath(`/profile/${userId}/business-trips`),
    },
    {
      key: "fuel",
      title: t("tasks_do_fuel_reports"),
      count: fuelReport.count,
      // The title of the task is the vehicle's name.
      rows: fuelReport.items.map((item) => (
        <TaskRow
          key={`fuel-${item.id}`}
          title={t("tasks_fuel_report_title", { month: fuelMonthName(fuelTaskPeriod(item.link), t) })}
          meta={item.title}
          action={{ label: t("cabinet_action_fill"), onClick: () => navigate(item.link) }}
        />
      )),
      allPath: companyPath(`/profile/${userId}/fuel-reports`),
    },
  ];

  // An expiring item's id is its document's id, its dueDate the document's end date (always set).
  const expiringRows = expiring.items.map((item) => {
    const document = { fileId: item.id, companyId: item.companyId, dateTo: item.dueDate ?? "" };
    return (
      <TaskRow
        key={`expiring-${item.id}`}
        title={item.title}
        meta={expiryStatus(document.dateTo, t).label}
        chips={otherCompanyChip(item)}
        action={{ label: t("tasks_action_open"), onClick: () => navigate(item.link), quiet: true }}
        textAction={{ label: t("expiry_handled_action"), onClick: () => markExpiryHandled(document) }}
      />
    );
  });

  const hasNothing =
    sign.count === 0 &&
    signWorkCount === 0 &&
    expiring.count === 0 &&
    toDoParts.every((part) => part.count === 0) &&
    notInApp.count === 0;
  if (hasNothing) return null;

  return (
    <TasksSection title={t("tasks_section_mine")}>
      <TaskGroup
        title={t("tasks_group_sign")}
        parts={[
          {
            key: "sign",
            count: sign.count,
            rows: sign.items.map(signRow),
            allPath: companyPath("/my-work/pending-signature?type=mine"),
          },
          signWorkPart(),
        ]}
        maxRows={PREVIEW_ROWS}
        allLinkKey="tasks_show_all"
      />
      <TaskGroup title={t("tasks_group_do")} parts={toDoParts} allLinkKey="tasks_all_of_kind" />
      <TaskGroup
        title={t("tasks_group_expiring")}
        parts={[
          {
            key: "expiring",
            count: expiring.count,
            rows: expiringRows,
            allPath: companyPath("/my-work/expiring-documents"),
          },
        ]}
        maxRows={PREVIEW_ROWS}
        allLinkKey="tasks_show_all"
      />
      {notInApp.count > 0 && (
        <p className="text-xs text-gray-500">{t("tasks_not_in_app", { count: notInApp.count })}</p>
      )}
    </TasksSection>
  );
}
